import { type FC, type PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { _isNil, _notNil } from '@/littledash';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { type ID } from '@/model/Common.model';
import { type StudyApiId } from '@/model/Study.model';
import {
  type GroupDimension,
  type MetaField,
  type ReservedSampleAggregation,
  type ReservedSample,
  type SampleDefinitionMap,
  type GroupMap,
  type GroupByNumberMap,
  type SampleTypeMap,
  type AnimalMap,
  type ChildData,
  type SampleNamesTableContext,
  type RootKey,
  type Level1Key,
  type Level2Key,
  type LeafKey,
  toLevel1Key,
  toLevel2Key,
  isMetaGroupDimension,
} from './SampleNames.model';
import { buildAggQuery, STATIC_DIMENSION_FILTER_KEY, NO_STUDY_GROUP_KEY } from './SampleNames.utils';
import { sampleNamesTableContext } from './SampleNamesTableContext';

/**
 * Provides the SampleNamesTable context: manages open/collapsed tree state, lazily
 * fetches child aggregations and leaf sample names on expand, and exposes toggle
 * callbacks for all three tree depths.
 */
export const SampleNamesTableProvider: FC<
  PropsWithChildren<{
    studyApiId: StudyApiId;
    studyId: ID;
    groupMap: GroupMap;
    groupings: Array<GroupDimension>;
    metaFields: Array<MetaField>;
    sampleTypeMap: SampleTypeMap;
    animalMap: AnimalMap;
    sampleDefinitionMap: SampleDefinitionMap;
  }>
> = ({
  children,
  studyApiId,
  studyId,
  groupMap,
  groupings,
  metaFields,
  sampleTypeMap,
  animalMap,
  sampleDefinitionMap,
}) => {
  const groupByNumberMap = useMemo<GroupByNumberMap>(() => {
    const m: GroupByNumberMap = new Map();
    groupMap.forEach((group) => {
      if (_notNil(group.no)) {
        m.set(group.no, group);
      }
    });
    return m;
  }, [groupMap]);

  const animalCountByGroup = useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    animalMap.forEach((entry) => {
      if (entry.type === 'registered' && _notNil(entry.study_group_api_id)) {
        counts[entry.study_group_api_id] = (counts[entry.study_group_api_id] ?? 0) + 1;
      }
    });
    return counts;
  }, [animalMap]);

  const [openRoots, setOpenRoots] = useState<Set<RootKey>>(() => new Set());
  const [openLevel1, setOpenLevel1] = useState<Set<Level1Key>>(() => new Set());
  const [openLevel2, setOpenLevel2] = useState<Set<Level2Key>>(() => new Set());
  const [level1Children, setLevel1Children] = useState<Map<RootKey, ChildData<Array<ReservedSampleAggregation>>>>(
    () => new Map()
  );
  const [level2Children, setLevel2Children] = useState<Map<Level1Key, ChildData<Array<ReservedSampleAggregation>>>>(
    () => new Map()
  );
  const [leafNames, setLeafNames] = useState<Map<LeafKey, ChildData<Array<ReservedSample>>>>(() => new Map());

  const { invoke: fetchAggregations } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/reserved-samples/aggregations',
    path: { studyApiId },
    options: { invokeOnInit: false, onError: { slug: 'sample-names-aggregations-load', toast: false } },
  });

  const { invoke: fetchNames } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/reserved-samples',
    path: { studyApiId },
    options: { invokeOnInit: false, onError: { slug: 'sample-names-load', toast: false } },
  });

  /** Builds query params for the flat reserved-samples list, applying all active grouping dimensions as filters. */
  const buildLeafQuery = useCallback(
    (keys: Array<string>) => {
      const query: Record<string, string | number | boolean> = { perPage: -1 };
      groupings.forEach((dimension, i) => {
        const key = keys[i];
        if (_notNil(key)) {
          if (isMetaGroupDimension(dimension)) {
            query['meta_glossary_api_id'] = dimension.apiId;
            query['meta_value'] = key;
          } else if (dimension === 'studyGroup' && key === NO_STUDY_GROUP_KEY) {
            query['no_study_group'] = true;
          } else {
            query[STATIC_DIMENSION_FILTER_KEY[dimension]] = key;
          }
        }
      });
      return query;
    },
    [groupings]
  );

  const updateLeafNames = useCallback(
    (cacheKey: LeafKey, keys: Array<string>) => {
      const existingLeaf = leafNames.get(cacheKey);
      if (_notNil(existingLeaf) && existingLeaf.status !== 'error') {
        return;
      }
      setLeafNames((prev) => new Map(prev).set(cacheKey, { status: 'loading' }));
      fetchNames({
        path: { studyApiId },
        query: buildLeafQuery(keys),
      })
        .then((response) => {
          setLeafNames((prev) => {
            const next = new Map(prev);
            if (response.type === 'success') {
              const body = response.body as { data: Array<ReservedSample> };
              next.set(cacheKey, { status: 'loaded', data: body.data });
            } else {
              next.set(cacheKey, { status: 'error', error: response });
            }
            return next;
          });
        })
        .catch((error) => {
          setLeafNames((prev) => new Map(prev).set(cacheKey, { status: 'error', error }));
        });
    },
    [studyApiId, leafNames, fetchNames, buildLeafQuery]
  );

  const toggleRoot = useCallback(
    (rootKey: RootKey) => {
      setOpenRoots((prev) => {
        const next = new Set(prev);
        if (next.has(rootKey)) {
          next.delete(rootKey);
        } else {
          next.add(rootKey);
        }
        return next;
      });

      const nextGroupBy = groupings[1];

      if (_isNil(nextGroupBy)) {
        updateLeafNames(rootKey, [rootKey]);
        return;
      }

      const existingLevel1 = level1Children.get(rootKey);
      if (_notNil(existingLevel1) && existingLevel1.status !== 'error') {
        return;
      }

      setLevel1Children((prev) => new Map(prev).set(rootKey, { status: 'loading' }));

      fetchAggregations({
        path: { studyApiId },
        query: buildAggQuery(nextGroupBy, [{ dimension: groupings[0], key: rootKey }]),
      })
        .then((response) => {
          setLevel1Children((prev) => {
            const next = new Map(prev);
            if (response.type === 'success') {
              next.set(rootKey, {
                status: 'loaded',
                data: (response.body as { data: Array<ReservedSampleAggregation> }).data,
              });
            } else {
              next.set(rootKey, { status: 'error', error: response });
            }
            return next;
          });
        })
        .catch((error) => {
          setLevel1Children((prev) => new Map(prev).set(rootKey, { status: 'error', error }));
        });
    },
    [studyApiId, groupings, level1Children, updateLeafNames, fetchAggregations]
  );

  const toggleLevel1 = useCallback(
    (rootKey: RootKey, level1KeyRaw: string) => {
      const cacheKey = toLevel1Key(rootKey, level1KeyRaw);
      setOpenLevel1((prev) => {
        const next = new Set(prev);
        if (next.has(cacheKey)) {
          next.delete(cacheKey);
        } else {
          next.add(cacheKey);
        }
        return next;
      });

      const nextGroupBy = groupings[2];

      if (_notNil(nextGroupBy)) {
        const existingLevel2 = level2Children.get(cacheKey);
        if (_notNil(existingLevel2) && existingLevel2.status !== 'error') {
          return;
        }
        setLevel2Children((prev) => new Map(prev).set(cacheKey, { status: 'loading' }));
        fetchAggregations({
          path: { studyApiId },
          query: buildAggQuery(nextGroupBy, [
            { dimension: groupings[0], key: rootKey },
            { dimension: groupings[1], key: level1KeyRaw },
          ]),
        })
          .then((response) => {
            setLevel2Children((prev) => {
              const next = new Map(prev);
              if (response.type === 'success') {
                next.set(cacheKey, {
                  status: 'loaded',
                  data: (response.body as { data: Array<ReservedSampleAggregation> }).data,
                });
              } else {
                next.set(cacheKey, { status: 'error', error: response });
              }
              return next;
            });
          })
          .catch((error) => {
            setLevel2Children((prev) => new Map(prev).set(cacheKey, { status: 'error', error }));
          });
      } else {
        updateLeafNames(cacheKey, [rootKey, level1KeyRaw]);
      }
    },
    [studyApiId, groupings, level2Children, updateLeafNames, fetchAggregations]
  );

  const toggleLevel2 = useCallback(
    (rootKey: RootKey, level1KeyRaw: string, level2KeyRaw: string) => {
      const cacheKey = toLevel2Key(rootKey, level1KeyRaw, level2KeyRaw);
      setOpenLevel2((prev) => {
        const next = new Set(prev);
        if (next.has(cacheKey)) {
          next.delete(cacheKey);
        } else {
          next.add(cacheKey);
        }
        return next;
      });

      updateLeafNames(cacheKey, [rootKey, level1KeyRaw, level2KeyRaw]);
    },
    [updateLeafNames]
  );

  const value = useMemo<SampleNamesTableContext>(
    () => ({
      studyApiId,
      studyId,
      groupMap,
      groupByNumberMap,
      animalCountByGroup,
      groupings,
      metaFields,
      sampleTypeMap,
      animalMap,
      sampleDefinitionMap,
      openRoots,
      openLevel1,
      openLevel2,
      level1Children,
      level2Children,
      leafNames,
      toggleRoot,
      toggleLevel1,
      toggleLevel2,
    }),
    [
      studyApiId,
      studyId,
      groupMap,
      groupByNumberMap,
      animalCountByGroup,
      groupings,
      metaFields,
      sampleTypeMap,
      animalMap,
      sampleDefinitionMap,
      openRoots,
      openLevel1,
      openLevel2,
      level1Children,
      level2Children,
      leafNames,
      toggleRoot,
      toggleLevel1,
      toggleLevel2,
    ]
  );

  return <sampleNamesTableContext.Provider value={value}>{children}</sampleNamesTableContext.Provider>;
};
